import React, { useState } from 'react';
import { useStaff, useBaseCurrency } from '../../../db/hooks/useLookupData';
import { ArrowLeft, Trash2, User, UserPlus, Check, ChevronDown, Tag } from 'lucide-react';
import { Button } from '../../common/Button';
import type { POSCartItem } from '../../../stores/pos.store';

interface POSEditCartItemViewProps {
  item: POSCartItem;
  onBack: () => void;
  onSave: (updates: { operatorId?: string; discountAmount?: number }) => void;
  onDelete: () => void;
  onAddNewStaff: () => void;
}

export function POSEditCartItemView({
  item,
  onBack,
  onSave,
  onDelete,
  onAddNewStaff,
}: POSEditCartItemViewProps) {
  const { staff = [] } = useStaff();
  const { currencySymbol, baseCurrency } = useBaseCurrency();

  const [selectedOperatorId, setSelectedOperatorId] = useState<string>(item.operatorId || '');
  const [discountAmount, setDiscountAmount] = useState<string>(
    item.discountAmount ? String(item.discountAmount) : '0'
  );
  const [isStaffDropdownOpen, setIsStaffDropdownOpen] = useState(false);

  const isPackage = item.type === 'package' || Boolean(item.package);
  const title = isPackage ? (item.package?.name || 'Combo') : (item.service?.concept || 'Servicio');
  const duration = !isPackage ? `${item.service?.duration_minutes || 30} min` : 'Paquete';
  const basePrice = item.unitPrice;
  const numericDiscount = Math.max(0, parseFloat(discountAmount) || 0);
  const finalPrice = Math.max(0, basePrice - numericDiscount);

  const currentOperator = staff.find((p) => p.id === selectedOperatorId);

  const handleApplyPresetPercent = (percent: number) => {
    const calculated = (basePrice * percent) / 100;
    setDiscountAmount(calculated.toFixed(2));
  };

  const handleSave = () => {
    onSave({
      operatorId: selectedOperatorId || undefined,
      discountAmount: numericDiscount,
    });
    onBack();
  };

  return (
    <div className="flex flex-col h-full animate-fade-in bg-surface overflow-hidden">
      {/* ── Top Header with Back button ── */}
      <div className="p-3.5 sm:p-6 pb-3 border-b border-border bg-surface-secondary/40 shrink-0">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-surface border border-border hover:bg-surface-tertiary text-xs font-extrabold text-text-primary transition-colors cursor-pointer mb-2.5 shadow-xs"
        >
          <ArrowLeft className="w-3.5 h-3.5 text-brand-sky" />
          <span>Atrás</span>
        </button>
        <h3 className="text-base sm:text-lg font-black text-text-primary">
          Editar {isPackage ? 'combo' : 'servicio'}
        </h3>
      </div>

      {/* ── Scrollable Form Area ── */}
      <div className="flex-1 overflow-y-auto custom-scrollbar p-3.5 sm:p-6 space-y-4 sm:space-y-5">
        {/* Item Banner Card */}
        <div className="p-3.5 bg-surface-secondary border-l-4 border-l-brand-sky border border-border rounded-2xl shadow-xs flex items-center justify-between">
          <div>
            <span className="font-extrabold text-sm text-text-primary block">{title}</span>
            <span className="text-xs text-text-secondary font-medium">{duration}</span>
          </div>
          <span className="text-sm font-black text-brand-orange">
            {currencySymbol}{basePrice.toFixed(2)} {baseCurrency}
          </span>
        </div>

        {/* Miembro del equipo (Esteticista) */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-extrabold text-text-secondary uppercase tracking-wider block">
              Miembro del equipo
            </label>
            <button
              type="button"
              onClick={onAddNewStaff}
              className="text-[11px] font-bold text-brand-sky hover:underline flex items-center gap-1 cursor-pointer"
            >
              <UserPlus className="w-3 h-3" />
              + Registrar personal
            </button>
          </div>

          <div className="relative">
            <button
              type="button"
              onClick={() => setIsStaffDropdownOpen(!isStaffDropdownOpen)}
              className="w-full flex items-center justify-between p-3 rounded-xl bg-surface-secondary border border-border hover:border-brand-sky/50 transition-all text-left cursor-pointer"
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="w-8 h-8 rounded-xl bg-brand-sky/10 border border-brand-sky/20 flex items-center justify-center text-brand-sky font-black text-xs shrink-0">
                  {currentOperator ? currentOperator.name.charAt(0).toUpperCase() : '?'}
                </div>
                <div className="min-w-0">
                  <span className="font-extrabold text-xs text-text-primary block truncate">
                    {currentOperator ? currentOperator.name : 'Seleccionar esteticista...'}
                  </span>
                  <span className="text-[10px] text-text-tertiary block capitalize">
                    {currentOperator?.role || 'Sin asignar'}
                  </span>
                </div>
              </div>
              <ChevronDown
                className={`w-4 h-4 text-text-tertiary transition-transform ${
                  isStaffDropdownOpen ? 'rotate-180' : ''
                }`}
              />
            </button>

            {isStaffDropdownOpen && (
              <div className="mt-1.5 p-1.5 bg-surface border border-border rounded-xl shadow-xl space-y-1 max-h-48 overflow-y-auto custom-scrollbar animate-scale-in">
                {staff.map((p) => {
                  const isSelected = selectedOperatorId === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => {
                        setSelectedOperatorId(p.id);
                        setIsStaffDropdownOpen(false);
                      }}
                      className={`w-full flex items-center justify-between p-2 rounded-lg text-left transition-colors cursor-pointer ${
                        isSelected
                          ? 'bg-brand-sky/15 text-brand-sky font-bold'
                          : 'hover:bg-surface-tertiary text-text-primary'
                      }`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-6 h-6 rounded-md bg-surface-secondary flex items-center justify-center text-[10px] font-bold shrink-0">
                          {p.name.charAt(0).toUpperCase()}
                        </div>
                        <span className="text-xs truncate">{p.name}</span>
                      </div>
                      {isSelected && <Check className="w-3.5 h-3.5 text-brand-sky shrink-0" />}
                    </button>
                  );
                })}
                {staff.length === 0 && (
                  <p className="text-center text-xs text-text-tertiary py-3 font-semibold">
                    No hay personal registrado.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Precio & Descuento Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 pt-1">
          {/* Precio del servicio */}
          <div className="space-y-1.5">
            <label className="text-xs font-extrabold text-text-secondary uppercase tracking-wider block">
              Precio del servicio
            </label>
            <div className="relative">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-xs font-bold text-text-tertiary">
                {currencySymbol}
              </span>
              <input
                type="text"
                disabled
                value={basePrice.toFixed(2)}
                className="w-full pl-8 pr-3 py-2.5 bg-surface-secondary/60 border border-border rounded-xl text-xs font-black text-text-primary opacity-80 cursor-not-allowed"
              />
            </div>
          </div>

          {/* Descuento */}
          <div className="space-y-1.5">
            <label className="text-xs font-extrabold text-text-secondary uppercase tracking-wider block flex items-center justify-between">
              <span>Descuento ({currencySymbol})</span>
              {numericDiscount > 0 && (
                <button
                  type="button"
                  onClick={() => setDiscountAmount('0')}
                  className="text-[10px] font-bold text-text-tertiary hover:text-error transition-colors cursor-pointer"
                >
                  Quitar
                </button>
              )}
            </label>
            <div className="relative">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-xs font-bold text-text-tertiary">
                {currencySymbol}
              </span>
              <input
                type="number"
                inputMode="decimal"
                min="0"
                max={basePrice}
                step="0.01"
                placeholder="0.00"
                value={discountAmount}
                onChange={(e) => setDiscountAmount(e.target.value)}
                className="w-full pl-8 pr-3 py-2.5 bg-surface-secondary border border-border rounded-xl text-xs font-black text-text-primary focus:border-brand-sky outline-none"
              />
            </div>
          </div>
        </div>

        {/* Quick Discount Percent Chips */}
        <div className="space-y-1.5 pt-1">
          <span className="text-[10px] font-bold text-text-tertiary block uppercase tracking-wider">
            Descuento rápido:
          </span>
          <div className="flex items-center gap-1.5 flex-wrap">
            <button
              type="button"
              onClick={() => setDiscountAmount('0')}
              className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-colors cursor-pointer border ${
                numericDiscount === 0
                  ? 'bg-surface-secondary text-text-primary border-border font-extrabold'
                  : 'bg-surface hover:bg-surface-secondary text-text-secondary border-border/50'
              }`}
            >
              Sin descuento
            </button>
            {[5, 10, 15, 20, 50].map((pct) => {
              const pctAmount = ((basePrice * pct) / 100).toFixed(2);
              const isSelected = discountAmount === pctAmount;
              return (
                <button
                  key={pct}
                  type="button"
                  onClick={() => handleApplyPresetPercent(pct)}
                  className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-colors cursor-pointer border ${
                    isSelected
                      ? 'bg-brand-orange text-white border-brand-orange shadow-xs'
                      : 'bg-surface hover:bg-brand-orange/10 hover:text-brand-orange text-text-secondary border-border/50'
                  }`}
                >
                  {pct}%
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Sticky Bottom Actions Bar (Fresha-style) ── */}
      <div className="p-3.5 sm:p-5 border-t border-border bg-surface-secondary/70 backdrop-blur-md flex items-center justify-between gap-3 shrink-0">
        <button
          type="button"
          onClick={onDelete}
          title="Eliminar este ítem del carrito"
          aria-label="Eliminar servicio"
          className="p-2.5 sm:p-3 rounded-xl border border-border hover:bg-error/10 hover:border-error/30 text-text-tertiary hover:text-error transition-colors cursor-pointer shrink-0"
        >
          <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>

        <div className="flex items-center gap-3">
          <div className="text-right">
            <span className="text-[10px] font-bold text-text-tertiary block uppercase">Total</span>
            <span className="text-xs sm:text-sm font-black text-text-primary block">
              {duration} • <span className="text-brand-orange">{currencySymbol}{finalPrice.toFixed(2)}</span>
            </span>
          </div>

          <Button
            variant="primary"
            size="md"
            onClick={handleSave}
            className="bg-brand-orange hover:bg-brand-orange/90 text-white font-black px-5 sm:px-6 rounded-xl shadow-md shadow-brand-orange/20"
          >
            Aplicar
          </Button>
        </div>
      </div>
    </div>
  );
}
